import { flatMap } from "lodash";
import React, { JSX } from "react";
import { ProviderSetCargoColumnSchema } from "@app/provider-sets";
import { AccuracyTemplate, AccuracyTemplateInspectionGroup, AccuracyTemplateScoringBand } from "@app/templates/accuracy/models";
import { EntityTypeEnum } from "@app/projects/enumerators";
import { NPF } from "@app/projects/servicing-provider/types";
import { getEntityIcon, getEntityTypeDescription } from "@app/projects/servicing-provider/ServicingProviderHelpers";
import AccuracyInspectionIcon from "@app/projects/servicing-provider/accuracy/AccuracyInspectionIcon";
import { AccuracyProviderProfile } from "@app/projects/servicing-provider/accuracy/AccuracyProviderProfile";
import { AccuracyNetworkProvidersPanelProps } from "@app/projects/servicing-provider/accuracy/accuracyNetworkProvidersPanelProps";
import { FlyoutLink } from "@app/shared/Flyouts";
import { filterProviderClaimsActivityInspectionGroup } from "@app/projects/network-assessment/accuracy/AccuracyProviderClaimsActivityFilter";
import { filterPractitionerAtLocationConfidenceInspectionGroup } from "@app/projects/network-assessment/accuracy/AccuracyPractitionerAtLocationConfidenceFilter";
import { ProviderNetworkTypeEnum } from "@app/shared/enums/providerNetworkTypeEnum";
import { filterDirectoryFacingInspectionGroup } from "@app/projects/network-assessment/accuracy/AccuracyDirectoryFacingFilter";
import { AccuracyProvider } from "@app/projects/results/accuracy/accuracyProvider";
import TooltipWrapper from "@app/shared/TooltipWrapper";
import formatPhoneNumber from "@app/shared/formatPhoneNumber";
import { restrictByFields } from "@app/viewable-fields/utilities";
import { ViewableField } from "@app/viewable-fields/viewableFields";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons";

class ElementNode {
  public element: React.ReactNode;
  public key: string;

  constructor(key: string, element: React.ReactNode) {
    this.element = element;
    this.key = key;
  }
}

export class AccuracyNetworkProviderResult {
  public key: string;
  public displayName: JSX.Element;
  public address: JSX.Element;
  public score: string;
  // `declare` prevents this from being defined on the instance ahead of the dynamically-assigned
  // inspection/cargo columns below (see constructor). Without it, TypeScript's class fields
  // (useDefineForClassFields, default for target esnext) would create this property immediately
  // after `score`, which shifts the actual rendered column order out of sync with the column
  // groups built in AccuracyNetworkProvidersPanel, causing header/value misalignment.
  public declare daysSinceLastAttested: JSX.Element;

  constructor(key: string, displayName: JSX.Element, address: JSX.Element, score: string, inspections: ElementNode[], daysSinceLastAttested: JSX.Element, cargoColumns: ElementNode[]) {
    /* oxlint-disable sort-keys */
    this.key = key;
    this.displayName = displayName;
    this.address = address;
    this.score = score;

    for (const inspection of inspections) {
      // key represents inspectionUID
      Object.assign(this, { [inspection.key]: inspection.element });
    }

    this.daysSinceLastAttested = daysSinceLastAttested;

    for (const cc of cargoColumns) {
      Object.assign(this, { [cc.key]: cc.element });
    }
    /* oxlint-enable sort-keys */
  }
}

export function getAccuracyProviderResults(template: AccuracyTemplate, providers: AccuracyProvider[], viewableFields: ViewableField[], props: AccuracyNetworkProvidersPanelProps, accuracyBands: AccuracyTemplateScoringBand[], cargoColumnsSchema: ProviderSetCargoColumnSchema[], hideProviderClaimsActivity = true, hidePracAtLocConfidence = true, hideDirectoryFacing = true, inspectionGroups?: AccuracyTemplateInspectionGroup[]): AccuracyNetworkProviderResult[] {
  let filteredInspectionGroups = inspectionGroups;

  if (!filteredInspectionGroups) {
    let mappedInspectionGroups: AccuracyTemplateInspectionGroup[] = filterProviderClaimsActivityInspectionGroup(template ? template.criteria.inspectionGroups : [], hideProviderClaimsActivity);
    mappedInspectionGroups = filterPractitionerAtLocationConfidenceInspectionGroup(template && mappedInspectionGroups ? mappedInspectionGroups : [], hidePracAtLocConfidence);
    mappedInspectionGroups = filterDirectoryFacingInspectionGroup(template && mappedInspectionGroups ? mappedInspectionGroups : [], hideDirectoryFacing);
    filteredInspectionGroups = mappedInspectionGroups;
  }

  const inspectionGroupsToRender = filteredInspectionGroups ?? [];

  const accuracyProviderResult: AccuracyNetworkProviderResult[] = providers.map((provider) => {
    const key: string = provider.providerId.toString();

    const name: JSX.Element = restrictByFields(viewableFields, [NPF.DisplayName, NPF.NPI, NPF.OrganizationName], (has) => (
      <>
        {has(NPF.DisplayName) && (
          <div className="text-nowrap">
            <FlyoutLink
              classNames="p-0 qes-button-secondary"
              content={{
                body: getProviderProfile(provider, props) as JSX.Element,
                title: "Provider Profile",
              }}
              testId={`accuracy-provider-result-rows__provider-profile-flyout--${provider.providerId}`}
            >
              <QesIcon iconName={getEntityIcon(provider.entityType)} description={getEntityTypeDescription(provider.entityType)} className="suppress-panel-styles align-text-top pr16" /> {provider.displayName}
            </FlyoutLink>
          </div>
        )}
        {has(NPF.NPI) && (
          <div className="text-nowrap">
            <span className="text-muted">NPI: {provider.npi}</span>
          </div>
        )}
        {has(NPF.OrganizationName)
          && provider.organizationName
          && provider.entityType === EntityTypeEnum.Individual.toLowerCase() && (
            <div className="text-nowrap">
              <span className="text-muted">{provider.organizationName}</span>
            </div>
          )}
      </>
    ));

    const address: JSX.Element = restrictByFields(viewableFields, [NPF.Address, NPF.Address2, NPF.City, NPF.State, NPF.Zip, NPF.Phone, NPF.Fax], (has) => (
      <>
        {has(NPF.Address) && provider.address ? <><span className="text-nowrap">{provider.address}</span><br /></> : null}
        {has(NPF.Address2) && provider.address2 ? <><span className="text-nowrap">{provider.address2}</span><br /></> : null}
        <span className="text-nowrap">
          {has(NPF.City) && provider.city}
          {has(NPF.City) && (has(NPF.State) || has(NPF.Zip)) ? ", " : null}
          {has(NPF.State) && provider.state} {has(NPF.Zip) && provider.zip}
        </span>
        {has(NPF.Phone) && provider.phone ? <br /> : null}
        {has(NPF.Phone) && provider.phone ?
          <span className="text-nowrap inline-tooltip">
            <TooltipWrapper text="Phone" position="bottom">
              <QesIcon iconName={QesIcons.PhoneV1} isDecorative className="suppress-panel-styles align-text-top" />
            </TooltipWrapper>
            &nbsp; {formatPhoneNumber(provider.phone, true)}
          </span> :
          null
        }
        {has(NPF.Fax) && provider.fax ? <br /> : null}
        {has(NPF.Fax) && provider.fax ?
          <span className="text-nowrap inline-tooltip">
            <TooltipWrapper text="Fax" position="bottom">
              <QesIcon iconName={QesIcons.Printer} isDecorative className="suppress-panel-styles align-text-top" />
            </TooltipWrapper>
            &nbsp; {formatPhoneNumber(provider.fax, true)}
          </span> :
          null
        }
      </>
    ));

    const score: string = provider.score.toFixed(1);

    const inspections: ElementNode[] = flatMap(inspectionGroupsToRender, (inspectionGroup) => inspectionGroup.inspections.map((inspection) => {
      const inspectionResult = provider.inspectionResults.find((ir) => ir.inspectionUid === inspection.uid);
      const filterResult = provider.entityType === 'facility' && inspection.uid === "C4A5AB3D-3499-431C-86A8-A9D3A0AD0E68"; // Practitioner At Location

      return ({
        element: (
          <>
            {
              inspectionResult !== undefined
                ? <AccuracyInspectionIcon accuracyTemplateScoringBands={accuracyBands} inspectionName={inspection.name} maxPoints={props.accuracyTemplate.scoring.maxPoints} pointStyle={props.accuracyTemplate.scoring.pointStyle} filterResult={filterResult} result={inspectionResult.result} resultJustification={inspectionResult.resultJustification} score={Number(inspectionResult.score)} weight={Number(inspectionResult.weight)} bandOverride={inspectionResult.bandOverride} />
                : "N/A"
            }
          </>
        ),
        key: inspection.uid,
      });
    }));

    const daysSinceLastAttested: JSX.Element = (
      <>
        {provider.daysSinceLastAttested != null && provider.daysSinceLastAttested <= props.runConfiguration.recentAttestationDays ? (
          <TooltipWrapper
            delay={200}
            text="Days since most recent attestation received; including validations, invalidations and enrichments."
            position="bottom"
          >
            <><i className="supress-panel-styles" /> {provider.daysSinceLastAttested} days</>
          </TooltipWrapper>
        ) : (<><i className="supress-panel-styles" /> Not Attested</>)
        }
      </>
    );

    const cargoColumns: ElementNode[] = [];
    if (provider.cargoData && provider.cargoData.length > 0) {
      const cargoColumnsSchemata: ProviderSetCargoColumnSchema[] = cargoColumnsSchema || [];
      provider.cargoData.map((item) => {
        const schema = cargoColumnsSchemata.find((s) => s.externalName === item.name);
        const cargoColumn: ElementNode = {
          element: restrictByFields(viewableFields, [schema.internalName], (has) => (
            <>
              {
                has(schema.internalName) && (
                  schema.type.toLowerCase() === "bool"
                    ? !!item.value ? <QesIcon iconName={QesIcons.Check} isDecorative className="align-text-top" /> : <></>
                    : <div>{item.value}</div>
                )
              }
            </>
          )),
          key: schema.externalName,
        };
        cargoColumns.push(cargoColumn);
      });
    }

    return new AccuracyNetworkProviderResult(key, name, address, score, inspections, daysSinceLastAttested, cargoColumns);
  });

  return accuracyProviderResult;
}

// TODO: this shouldn't need the AccuracyNetworkProvidersPanelProps, move shared state to state management
function getProviderProfile(provider: AccuracyProvider, props: AccuracyNetworkProvidersPanelProps): React.ReactNode {
  return (
    <AccuracyProviderProfile
      provider={provider}
      providerNetworkType={ProviderNetworkTypeEnum.Network}
      runConfigurationDate={props.runConfiguration.dateCreated}
      showAttestation={props.project.showAttestation}
    />
  );
}